import { ReactNode, createContext, useContext, useState } from "react";

import { SafeAreaView } from "react-native";
import { Portal, Snackbar } from "react-native-paper";
import { useTailwind } from "tailwind-rn";

type ContextType = {
  snackbarText: string;
  snackbarVisible: boolean;
  setSnackbarVisible: (visible: boolean) => void;
  showMessage: (value?: string) => void;
  updateMessage: (value: string) => void;
};

const Context = createContext<ContextType | null>(null);

export default function useSnackbar() {
  const ctx = useContext(Context);
  if (!ctx) throw new Error("Missing SnackbarProvider");
  return ctx;
}

export function SnackbarProvider({ children }: { children: ReactNode }) {
  const [snackbarVisible, setSnackbarVisible] = useState(false);
  const [snackbarText, setSnackbarText] = useState("");

  function showMessage(value?: string) {
    const message = value || "Producto agregado a la venta";
    setSnackbarText(message);
    setSnackbarVisible(true);
  }

  function updateMessage(value: string) {
    setSnackbarText(value);
  }

  return (
    <Context.Provider
      value={{
        showMessage,
        snackbarText,
        snackbarVisible,
        setSnackbarVisible,
        updateMessage,
      }}
    >
      {children}
    </Context.Provider>
  );
}

type Props = {
  text: string;
  visible: boolean;
  onDismiss: () => void;
};

const DURATION = 2000;

export function FloatingSnackbar({ text, visible, onDismiss }: Props) {
  const tailwind = useTailwind();

  return (
    <Portal>
      <SafeAreaView style={tailwind("absolute bottom-0 left-0 right-0")}>
        <Snackbar
          visible={visible}
          duration={DURATION}
          onDismiss={onDismiss}
          style={tailwind("bottom-16")}
          action={{ label: "✕", onPress: onDismiss }}
        >
          {text}
        </Snackbar>
      </SafeAreaView>
    </Portal>
  );
}

export function ContextAwareFloatingSnackbar() {
  const { snackbarText, snackbarVisible, setSnackbarVisible } = useSnackbar();
  return (
    <FloatingSnackbar
      text={snackbarText}
      visible={snackbarVisible}
      onDismiss={() => setSnackbarVisible(false)}
    />
  );
}
